import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Database, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const HARD = ["sleeve_types", "fabric_types", "colors", "sizes"];

const CONFIG = {
  stores: { title: "Toko", subtitle: "Toko = saluran penjualan. Toko tidak memiliki stok terpisah.", fields: ["name"] },
  marketplaces: { title: "Marketplace", subtitle: "Registri channel marketplace.", fields: ["name"] },
  fabric_types: { title: "Jenis Bahan", subtitle: "Cotton Combed 30s & 24s dan lainnya.", fields: ["code", "name"] },
  sleeve_types: { title: "Jenis Lengan", subtitle: "Standar kanonik: PENDEK — Tangan Pendek & PANJANG — Tangan Panjang.", fields: ["code", "name"] },
  colors: { title: "Warna", subtitle: "Master warna produk, dapat ditambah kapan saja.", fields: ["code", "name", "hex_code"] },
  sizes: { title: "Ukuran", subtitle: "Ukuran per kategori (Anak / Dewasa).", fields: ["code", "name", "size_category_code", "sort_order"] },
  units: { title: "Satuan", subtitle: "Master satuan inventory (Roll, Kg, Pcs, ...). Identitas satuan tidak membedakan huruf besar/kecil.", fields: ["code", "name"] },
};

export default function MasterCrud() {
  const { collection } = useParams();
  const { can } = useAuth();
  const cfg = CONFIG[collection] || { title: collection, subtitle: "", fields: ["code", "name"] };
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = () => { setRows(null); api.get(`/masters/${collection}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))); };
  useEffect(load, [collection]);
  const visible = rows ? rows.filter((r) => showInactive || (r.status || "active") === "active") : null;

  return (
    <div>
      <PageHeader title={cfg.title} subtitle={cfg.subtitle} testId={`master-${collection}-header`}>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-600" data-testid="toggle-show-inactive">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="switch-show-inactive" />
            Tampilkan Nonaktif
          </label>
          {can("masters.manage") && <Button onClick={() => setOpen(true)} data-testid="master-add-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> Tambah</Button>}
        </div>
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {!rows ? <Loading /> : visible.length === 0 ? <EmptyState title="Belum ada data" icon={Database} /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                {cfg.fields.map((f) => <TableHead key={f}>{labelFor(f)}</TableHead>)}
                {collection === "fabric_types" && <TableHead>Tracking</TableHead>}
                <TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {visible.map((r) => (
                  <TableRow key={r.id} data-testid={`master-row-${r.code || r.id}`}>
                    {cfg.fields.map((f) => (
                      <TableCell key={f} className={f === "code" ? "font-mono text-xs font-semibold" : ""}>
                        {f === "hex_code" && r[f] ? <span className="inline-flex items-center gap-2"><span className="h-4 w-4 rounded-full border border-slate-300" style={{ background: r[f] }} />{r[f]}</span> : (r[f] ?? "-")}
                      </TableCell>
                    ))}
                    {collection === "fabric_types" && <TableCell data-testid={`master-tracking-${r.code || r.id}`}>{r.tracking_mode === "CATCH_WEIGHT_ROLL" ? <span className="rounded bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">Catch Weight (Roll)</span> : <span className="text-xs text-slate-500">Standar</span>}</TableCell>}
                    <TableCell><StatusBadge status={r.status || "active"} label={(r.status || "active") === "active" ? "Aktif" : "Nonaktif"} /></TableCell>
                    <TableCell className="whitespace-nowrap">
                      {can("masters.manage") && <Button variant="ghost" size="sm" onClick={() => setEdit(r)} data-testid={`master-edit-${r.code || r.id}`}><Pencil className="h-4 w-4" /></Button>}
                      {HARD.includes(collection) && can("masters.delete") && <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => setDel(r)} data-testid={`master-delete-${r.code || r.id}`}><Trash2 className="h-4 w-4" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      {(open || edit) && <MasterDialog collection={collection} cfg={cfg} row={edit} onClose={() => { setOpen(false); setEdit(null); }} onSaved={() => { setOpen(false); setEdit(null); load(); }} />}
      {del && <DeleteMasterDialog collection={collection} row={del} onClose={() => setDel(null)} onDone={() => { setDel(null); load(); }} />}
    </div>
  );
}

function MasterDialog({ collection, cfg, row, onClose, onSaved }) {
  const [form, setForm] = useState({ name: row?.name || "", code: row?.code || "", hex_code: row?.hex_code || "", size_category_code: row?.size_category_code || "ADULT", sort_order: row?.sort_order ?? 0, status: row?.status || "active", tracking_mode: row?.tracking_mode || "STANDARD" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name) return toast.error("Nama wajib diisi");
    setSaving(true);
    try {
      const body = { name: form.name, status: form.status };
      if (cfg.fields.includes("code")) body.code = form.code;
      if (cfg.fields.includes("hex_code")) body.hex_code = form.hex_code;
      if (cfg.fields.includes("size_category_code")) { body.size_category_code = form.size_category_code; body.sort_order = Number(form.sort_order); }
      if (collection === "fabric_types") body.tracking_mode = form.tracking_mode;
      if (row) await api.put(`/masters/${collection}/${row.id}`, body);
      else await api.post(`/masters/${collection}`, body);
      toast.success("Tersimpan"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">{row ? "Ubah" : "Tambah"} {cfg.title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {cfg.fields.includes("code") && <div><Label className="text-xs text-slate-500">Kode</Label><Input className="mt-1 font-mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="master-code" /></div>}
          <div><Label className="text-xs text-slate-500">Nama</Label><Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="master-name" /></div>
          {cfg.fields.includes("hex_code") && <div><Label className="text-xs text-slate-500">Hex Warna</Label><div className="mt-1 flex gap-2"><Input type="color" className="h-10 w-14 p-1" value={form.hex_code || "#000000"} onChange={(e) => setForm({ ...form, hex_code: e.target.value })} /><Input value={form.hex_code} onChange={(e) => setForm({ ...form, hex_code: e.target.value })} data-testid="master-hex" /></div></div>}
          {cfg.fields.includes("size_category_code") && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-500">Kategori</Label>
                <Select value={form.size_category_code} onValueChange={(v) => setForm({ ...form, size_category_code: v })}><SelectTrigger className="mt-1" data-testid="master-sizecat"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ADULT">Dewasa</SelectItem><SelectItem value="KIDS">Anak</SelectItem></SelectContent></Select>
              </div>
              <div><Label className="text-xs text-slate-500">Urutan</Label><Input type="number" className="mt-1" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} /></div>
            </div>
          )}
          <div><Label className="text-xs text-slate-500">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger className="mt-1" data-testid="master-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Nonaktif</SelectItem></SelectContent></Select>
          </div>
          {collection === "fabric_types" && (
            <div><Label className="text-xs text-slate-500">Mode Tracking</Label>
              <Select value={form.tracking_mode} onValueChange={(v) => setForm({ ...form, tracking_mode: v })}><SelectTrigger className="mt-1" data-testid="master-tracking-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="STANDARD">Standar</SelectItem><SelectItem value="CATCH_WEIGHT_ROLL">Catch Weight (Roll)</SelectItem></SelectContent></Select>
              <p className="mt-0.5 text-[10px] text-slate-400">Catch Weight (Roll): Production Order wajib isi Kebutuhan Kain (Roll).</p></div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="master-save-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "Menyimpan..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const labelFor = (f) => ({ code: "Kode", name: "Nama", hex_code: "Warna", size_category_code: "Kategori", sort_order: "Urutan" }[f] || f);

function DeleteMasterDialog({ collection, row, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [blockMsg, setBlockMsg] = useState("");
  const hardDelete = async () => {
    setBusy(true); setBlockMsg("");
    try { await api.delete(`/masters/${collection}/${row.id}/purge`); toast.success("Master dihapus permanen"); onDone(); }
    catch (e) {
      if (e.response?.status === 409) setBlockMsg(apiError(e));
      else toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  const deactivate = async () => {
    setBusy(true);
    try { await api.delete(`/masters/${collection}/${row.id}`); toast.success("Master dinonaktifkan"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const status = (row.status || "active") === "active" ? "Aktif" : "Nonaktif";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="master-delete-dialog">
        <DialogHeader><DialogTitle className="font-display">Hapus Master?</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Data hanya dapat dihapus jika belum pernah digunakan oleh Product Variant atau transaksi. Tindakan ini tidak dapat dibatalkan.</p>
        <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-3 text-sm">
          <div className="flex justify-between py-0.5"><span className="text-slate-500">Kode</span><span className="font-mono font-semibold">{row.code || "-"}</span></div>
          <div className="flex justify-between py-0.5"><span className="text-slate-500">Nama</span><span>{row.name}</span></div>
          <div className="flex justify-between py-0.5"><span className="text-slate-500">Status</span><span>{status}</span></div>
        </div>
        {blockMsg && (
          <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="master-delete-blocked">
            {blockMsg}
            <div className="mt-2"><Button size="sm" variant="outline" onClick={deactivate} disabled={busy} data-testid="master-deactivate-btn">Nonaktifkan saja</Button></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="master-delete-cancel">Batal</Button>
          {!blockMsg && <Button onClick={hardDelete} disabled={busy} className="bg-rose-600 hover:bg-rose-500" data-testid="master-delete-confirm">{busy ? "..." : "Hapus Permanen"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
